import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router-dom';

/**
 * Two everyday styles and two destructive ones (direction AC): primary is the accent fill,
 * secondary is quiet with a hairline. A destructive row opener is `danger-text` (the error colour,
 * no border); the confirming button inside a confirm step is the outlined `danger` (S-22, L-17).
 */
type Variant = 'primary' | 'secondary' | 'danger' | 'danger-text';
type Size = 'sm' | 'md';

const BASE =
  'inline-flex items-center justify-center gap-2 rounded-[10px] font-medium leading-none transition-colors duration-150 ease-ea disabled:cursor-not-allowed disabled:opacity-50 whitespace-nowrap';
/* `sm` keeps the 44px hit height; only the type and padding get smaller. */
const SIZES: Record<Size, string> = { sm: 'h-11 px-3 text-[13px]', md: 'h-11 px-4 text-[15px]' };
const VARIANTS: Record<Variant, string> = {
  primary: 'bg-accent text-onAccent hover:bg-accentHover',
  secondary: 'border border-rule2 bg-panel text-ink hover:border-accent hover:text-accentText',
  danger: 'border border-danger/50 bg-transparent text-dangerText hover:bg-danger hover:text-onDanger',
  'danger-text': 'bg-transparent text-dangerText underline-offset-4 hover:underline',
};

const classes = (variant: Variant, size: Size, block: boolean, className: string) =>
  `${BASE} ${SIZES[size]} ${VARIANTS[variant]} ${block ? 'w-full' : ''} ${className}`;

type ButtonProps = {
  variant?: Variant;
  size?: Size;
  block?: boolean;
  busy?: boolean;
  children: ReactNode;
} & ButtonHTMLAttributes<HTMLButtonElement>;

export function Button({
  variant = 'primary',
  size = 'md',
  block = false,
  busy = false,
  children,
  className = '',
  type = 'button',
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={classes(variant, size, block, className)}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      {...rest}
    >
      {children}
    </button>
  );
}

type LinkButtonProps = {
  variant?: Variant;
  size?: Size;
  block?: boolean;
  children: ReactNode;
} & LinkProps;

/** A router link styled as a button, for navigation that reads as an action. */
export function LinkButton({
  variant = 'primary',
  size = 'md',
  block = false,
  children,
  className = '',
  ...rest
}: LinkButtonProps) {
  return (
    <Link className={classes(variant, size, block, className)} {...rest}>
      {children}
    </Link>
  );
}

type AnchorButtonProps = {
  variant?: Variant;
  size?: Size;
  block?: boolean;
  children: ReactNode;
} & AnchorHTMLAttributes<HTMLAnchorElement>;

/** A plain anchor styled as a button, for downloads and off-app destinations. */
export function AnchorButton({
  variant = 'secondary',
  size = 'md',
  block = false,
  children,
  className = '',
  ...rest
}: AnchorButtonProps) {
  return (
    <a className={classes(variant, size, block, className)} {...rest}>
      {children}
    </a>
  );
}
